import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, InputAdornment, MenuItem, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import configAuditApi, { ConfigAuditEntry, ConfigAuditFacets, ConfigAuditPage } from "../../apis/configAuditApi";
import { FONT, NEUTRAL, RADIUS, VIOLET } from "../../styles/brandTokens";
import ChangeLogEntry from "./ChangeLogEntry";
import { RANGE_PRESETS, RangePreset, dayLabel, entityTypeLabel, rangeStart } from "./configAuditFormat";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_RANGE: RangePreset = "30d";
const EMPTY_FACETS: ConfigAuditFacets = { entity_types: [], actors: [], customers: [] };

const controlSx = {
  "& .MuiInputBase-root": { fontFamily: FONT.sans, fontSize: 13, borderRadius: `${RADIUS.control}px`, bgcolor: NEUTRAL.surface },
  "& .MuiInputLabel-root": { fontFamily: FONT.sans, fontSize: 13 },
};
const menuItemSx = { fontFamily: FONT.sans, fontSize: 13 };
const pagerSx = { textTransform: "none", fontFamily: FONT.sans, borderColor: NEUTRAL.borderStrong, color: NEUTRAL.text };

interface DayGroup {
  day: string;
  rows: ConfigAuditEntry[];
}

function isRangePreset(value: string | null): value is RangePreset {
  return RANGE_PRESETS.some((preset) => preset.id === value);
}

function groupByDay(rows: ConfigAuditEntry[]): DayGroup[] {
  return rows.reduce<DayGroup[]>((groups, row) => {
    const day = dayLabel(row.created_at);
    const last = groups[groups.length - 1];
    if (last?.day === day) return [...groups.slice(0, -1), { day, rows: [...last.rows, row] }];
    return [...groups, { day, rows: [row] }];
  }, []);
}

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

export default function ConfigChangeLog() {
  const [params, setParams] = useSearchParams();
  const rangeParam = params.get("range");
  const range: RangePreset = isRangePreset(rangeParam) ? rangeParam : DEFAULT_RANGE;
  const entityType = params.get("type") || "";
  const actorEmail = params.get("actor") || "";
  const customerId = params.get("customer") || "";
  const query = params.get("q") || "";
  const page = Math.max(1, Number.parseInt(params.get("page") || "1", 10) || 1);

  const [searchInput, setSearchInput] = useState(query);
  const search = useDebounced(searchInput.trim(), SEARCH_DEBOUNCE_MS);
  const [facets, setFacets] = useState<ConfigAuditFacets>(EMPTY_FACETS);
  const [facetsError, setFacetsError] = useState<string | null>(null);
  const [result, setResult] = useState<ConfigAuditPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /** Filters live in the URL so a filtered view can be shared; any filter change returns to page 1. */
  const updateParams = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    if (!("page" in patch)) next.delete("page");
    setParams(next, { replace: true });
  };

  useEffect(() => {
    if (search !== query) updateParams({ q: search });
    // Only a settled search writes the URL; `updateParams` reads the current params when it runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    configAuditApi
      .facets()
      .then(setFacets)
      .catch((err: unknown) => setFacetsError(errorMessage(err, "The filter lists could not be loaded.")));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    configAuditApi
      .list({ entityType, actorEmail, customerId, search: query, from: rangeStart(range), page, limit: PAGE_SIZE })
      .then((data) => {
        if (!cancelled) setResult(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(errorMessage(err, "Failed to load the change log."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [entityType, actorEmail, customerId, query, range, page]);

  const groups = useMemo(() => groupByDay(result?.rows ?? []), [result]);
  const total = result?.total ?? 0;
  const firstShown = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastShown = Math.min(page * PAGE_SIZE, total);
  const hasFilters = Boolean(entityType || actorEmail || customerId || query || range !== DEFAULT_RANGE);

  return (
    <Box component="main" sx={{ bgcolor: NEUTRAL.page, minHeight: "100%", px: { xs: 2, md: 4 }, py: { xs: 2.5, md: 4 } }}>
      <Box component="header" sx={{ mb: 3, maxWidth: 760 }}>
        <Typography sx={{ fontFamily: FONT.sans, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: NEUTRAL.textMuted }}>
          TOOLS
        </Typography>
        <Typography component="h1" sx={{ fontFamily: FONT.sans, fontSize: 26, fontWeight: 800, color: NEUTRAL.text, mt: 0.25 }}>
          Change Log
        </Typography>
        <Typography sx={{ fontFamily: FONT.sans, fontSize: 13.5, color: NEUTRAL.textSecondary, mt: 0.75, lineHeight: 1.5 }}>
          Every configuration change made in this workspace: agents, integrations, playbooks, schedules, customers, Teams setup and
          Theo users. Secrets are never stored; a changed key or password shows as hidden.
        </Typography>
      </Box>

      <Box
        component="section"
        aria-label="Filters"
        sx={{
          display: "grid",
          gap: 1.25,
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "minmax(220px, 2fr) repeat(4, minmax(150px, 1fr)) auto" },
          mb: 2,
        }}
      >
        <TextField
          size="small"
          placeholder="Search summaries, names, people"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          inputProps={{ "aria-label": "Search the change log" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: NEUTRAL.textMuted }} />
              </InputAdornment>
            ),
          }}
          sx={controlSx}
        />
        <TextField
          select
          size="small"
          label="When"
          value={range}
          onChange={(event) => updateParams({ range: event.target.value === DEFAULT_RANGE ? "" : event.target.value })}
          sx={controlSx}
        >
          {RANGE_PRESETS.map((preset) => (
            <MenuItem key={preset.id} value={preset.id} sx={menuItemSx}>
              {preset.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="What" value={entityType} onChange={(event) => updateParams({ type: event.target.value })} sx={controlSx}>
          <MenuItem value="" sx={menuItemSx}>
            Everything
          </MenuItem>
          {facets.entity_types.map((type) => (
            <MenuItem key={type} value={type} sx={menuItemSx}>
              {entityTypeLabel(type)}
            </MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Who" value={actorEmail} onChange={(event) => updateParams({ actor: event.target.value })} sx={controlSx}>
          <MenuItem value="" sx={menuItemSx}>
            Anyone
          </MenuItem>
          {facets.actors.map((actor) => (
            <MenuItem key={actor.email} value={actor.email} sx={menuItemSx}>
              {actor.email}
              <Box component="span" sx={{ ml: 1, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", color: NEUTRAL.textMuted }}>
                {actor.role}
              </Box>
            </MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Customer" value={customerId} onChange={(event) => updateParams({ customer: event.target.value })} sx={controlSx}>
          <MenuItem value="" sx={menuItemSx}>
            Any customer
          </MenuItem>
          {facets.customers.map((customer) => (
            <MenuItem key={customer.id} value={String(customer.id)} sx={menuItemSx}>
              {customer.name || `Customer ${customer.id}`}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="text"
          disabled={!hasFilters}
          onClick={() => {
            setSearchInput("");
            setParams(new URLSearchParams(), { replace: true });
          }}
          sx={{
            fontFamily: FONT.sans,
            fontSize: 13,
            textTransform: "none",
            color: NEUTRAL.textSecondary,
            justifySelf: "start",
            "&:hover": { color: VIOLET.main, bgcolor: "transparent" },
          }}
        >
          Clear
        </Button>
      </Box>

      {facetsError && (
        <Alert severity="info" sx={{ mb: 2, fontFamily: FONT.sans }}>
          {facetsError} Search and the time range still work.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2, fontFamily: FONT.sans }}>
          {error}
        </Alert>
      )}

      <Box
        component="section"
        aria-label="Configuration changes"
        aria-busy={loading}
        sx={{ border: `1px solid ${NEUTRAL.border}`, borderRadius: `${RADIUS.well}px`, bgcolor: NEUTRAL.surface, overflow: "hidden" }}
      >
        {loading && !result && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={24} sx={{ color: VIOLET.main }} />
          </Box>
        )}
        {result && result.rows.length === 0 && (
          <Box sx={{ textAlign: "center", py: 8, px: 2 }}>
            <Typography sx={{ fontFamily: FONT.sans, fontSize: 14, fontWeight: 700, color: NEUTRAL.text }}>No changes found</Typography>
            <Typography sx={{ fontFamily: FONT.sans, fontSize: 13, color: NEUTRAL.textSecondary, mt: 0.5 }}>
              {hasFilters ? "Try a wider time range or clear the filters." : "Configuration changes will appear here as people make them."}
            </Typography>
          </Box>
        )}
        <Box sx={{ opacity: loading && result ? 0.55 : 1, transition: "opacity 150ms ease" }}>
          {groups.map((group, index) => (
            <Box key={group.day} component="section" aria-label={group.day}>
              <Typography
                component="h2"
                sx={{
                  fontFamily: FONT.sans,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: NEUTRAL.textMuted,
                  bgcolor: NEUTRAL.subtle,
                  borderTop: index === 0 ? 0 : `1px solid ${NEUTRAL.border}`,
                  borderBottom: `1px solid ${NEUTRAL.border}`,
                  px: 2,
                  py: 0.75,
                }}
              >
                {group.day}
              </Typography>
              <Box component="ul" sx={{ m: 0, p: 0 }}>
                {group.rows.map((entry) => (
                  <ChangeLogEntry key={entry.id} entry={entry} />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {total > 0 && (
        <Box component="nav" aria-label="Change log pages" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1.5, gap: 2 }}>
          <Typography sx={{ fontFamily: FONT.sans, fontSize: 12.5, color: NEUTRAL.textSecondary, fontVariantNumeric: "tabular-nums" }}>
            {firstShown}–{lastShown} of {total.toLocaleString()}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              disabled={page <= 1 || loading}
              onClick={() => updateParams({ page: page - 1 > 1 ? String(page - 1) : "" })}
              sx={pagerSx}
            >
              Newer
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={lastShown >= total || loading}
              onClick={() => updateParams({ page: String(page + 1) })}
              sx={pagerSx}
            >
              Older
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
